import React from "react";
import { motion } from "framer-motion";
import { Sun, Zap, ShieldCheck, Cable, Factory, Trophy } from "lucide-react";
import sanildefonso1 from "../assets/sanIldefonso/sanildefonso (1).jpg";
import sanildefonso2 from "../assets/sanIldefonso/sanildefonso (2).jpg";
import sanildefonso3 from "../assets/sanIldefonso/sanildefonso (3).jpg";
import sanildefonso4 from "../assets/sanIldefonso/sanildefonso (4).jpg";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";

function ProjectHeroSanIldefonso() {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />

      <section className="w-full bg-gradient-to-br from-black via-slate-950 to-slate-900 text-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* HERO HEADER */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-10"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <Trophy className="text-yellow-400" size={36} />
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
                15 MWp Bulacan Solar Farm Project
              </h1>
            </div>

            <p className="text-cyan-400 text-xl md:text-2xl font-semibold tracking-wide">
              San Ildefonso, Bulacan
            </p>

            <p className="mt-6 text-gray-300 max-w-2xl mx-auto text-lg">
              A flagship renewable energy installation delivering high-capacity
              solar generation with precision-engineered electrical systems and
              industry-grade reliability.
            </p>
          </motion.div>

          {/* PROJECT INFO SECTION */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-5 gap-4 mb-14 text-center"
          >
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Location</p>
              <p className="text-white font-semibold">San Ildefonso, Bulacan</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Owner</p>
              <p className="text-white font-semibold">Bulacan Solar Energy Corporation</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Start Date</p>
              <p className="text-white font-semibold">September 10, 2015</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Completion Date</p>
              <p className="text-white font-semibold">March 6, 2016</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Status</p>
              <p className="text-green-400 font-semibold">Completed</p>
            </div>
          </motion.div>

          {/* MAIN GRID */}
          <div className="grid md:grid-cols-3 gap-10 items-start">
            {/* LEFT SECTION */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="md:col-span-2 space-y-6"
            >
              {/* IMAGE GRID */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  sanildefonso1,
                  sanildefonso2,
                  sanildefonso3,
                  sanildefonso4,
                ].map((img, i) => (
                  <motion.img
                    key={i}
                    whileHover={{ scale: 1.05 }}
                    src={img}
                    className="h-44 w-full object-cover rounded-2xl"
                  />
                ))}
              </div>

              <button
                onClick={() => navigate("/gallery/san-ildefonso")}
                className="px-6 py-3 font-semibold text-yellow-400 bg-gray-800 rounded-lg hover:bg-yellow-400 hover:text-black transition"
              >
                View More ⚡
              </button>

              {/* SCOPE */}
              <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-300/5 p-8 rounded-3xl border border-yellow-400/30 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Sun className="text-yellow-400" size={28} />
                  <h2 className="text-3xl font-bold text-yellow-400">
                    Scope of Works
                  </h2>
                </div>

                <ul className="grid md:grid-cols-2 gap-4 text-gray-200 text-lg">
                  <li className="flex items-start gap-3">
                    <Zap className="text-yellow-400 mt-1" />
                    Perimeter Lights & CCTV Systems
                  </li>
                  <li className="flex items-start gap-3">
                    <Cable className="text-yellow-400 mt-1" />
                    Electro-Mechanical Installation
                  </li>
                  <li className="flex items-start gap-3">
                    <Factory className="text-yellow-400 mt-1" />
                    MV and DC System Installation
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="text-yellow-400 mt-1" />
                    Testing and Commissioning
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* RIGHT SECTION */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-cyan-500/10 to-cyan-300/5 p-8 rounded-3xl border border-cyan-400/30 shadow-2xl"
            >
              <h2 className="text-3xl font-bold text-cyan-400 mb-8">
                Project Structure
              </h2>

              <ul className="space-y-6 text-lg text-gray-200">
                <li className="flex gap-3">
                  <span className="text-green-400 text-xl">✔</span>
                  Electrical Installation of Control Buildings
                </li>
                <li className="flex gap-3">
                  <span className="text-green-400 text-xl">✔</span>
                  Substation Development
                </li>
                <li className="flex gap-3">
                  <span className="text-green-400 text-xl">✔</span>
                  Lightning Protection Systems
                </li>
                <li className="flex gap-3">
                  <span className="text-green-400 text-xl">✔</span>
                  Structural Foundations
                </li>
              </ul>

              <div className="mt-10 p-4 bg-black/40 rounded-xl border border-cyan-500/20 text-center">
                <p className="text-sm text-gray-400">Project Capacity</p>
                <p className="text-2xl font-bold text-cyan-300">
                  15 MWp Solar Output
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default ProjectHeroSanIldefonso;
